<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { isNil, omit } from 'lodash-es'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import store from '@/store'
import { SERVICES_URL } from '@/main'

import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import CombinedPredictTableControls from '@/components/combined-predict/components/CombinedPredictTableControls.vue'
import ReportControls from '@/components/ReportControls.vue'

import NestedCategorySelect from '@/components/categorized-nested-select/NestedCategorySelect.vue'
import CombinedPredictTable from './components/CombinedPredictTable/CombinedPredictTable.vue'

import {
  buildCombinedRows,
  getCombinedProgress,
  getCombinedIsLoading,
  getCombinedIsAborted,
  getCombinedProgressLabels,
  fetchSelectedModels,
  resetSelectedModels,
  abortSelectedModels,
  getAllCategorizedModels,
} from './modelAdapterService'
import { rowHasAnyError, rowHasAnyEstimate } from './utils'
import { formatModelErrorsForDisplay } from '@/components/combined-predict/components/CombinedPredictTable/utils/formatModelErrorsForDisplay'
import { filterExistingColumnIds } from './modelAdapterService'
import { getGroupableModelIds } from './components/CombinedPredictTable/utils/regroupByProperty'
import { getChemicalTitle } from '@/utils/chemical'
import { useLocalStorage } from '@vueuse/core'

const checkedStructuresWithCartPosition = computed(() =>
  store.getters.getCheckedStructures.map((structure, index) => ({
    ...structure,
    // uuid: crypto.randomUUID(),
    inputOrder: index,
  })),
)

const defaultDisplaySettings = {
  showExperimental: true,
  showPredicted: true,
  showChemicalsWithNoEstimates: false,
  showChemCell: false,
  showVirtualized: false,
  isGroupedByProperty: false,
}

const displaySettings = useLocalStorage('combined-predict.display-settings', defaultDisplaySettings, {
  mergeDefaults: true,
})

const persistedSelectedColumnIds = useLocalStorage('combined-predict.column-ids', [])

const selectedColumnIds = computed({
  get: () => filterExistingColumnIds(persistedSelectedColumnIds.value),
  set: (value) => {
    persistedSelectedColumnIds.value = filterExistingColumnIds(Array.isArray(value) ? value : [])
  },
})

const selectedBranches = ref([])

const getSelectedBranches = (nodes) => {
  selectedBranches.value = [...nodes]
}

const activeModelIds = computed(() => {
  const collectModelIds = (node) => {
    if (!node) return []
    if (!isNil(node?.modelId)) return [node.modelId]
    if (Array.isArray(node?.children)) return node.children.flatMap(collectModelIds)
    return []
  }

  return selectedBranches.value.flatMap(collectModelIds)
})

const categorizedModels = computed(() => getAllCategorizedModels())

const combinedRows = computed(() => buildCombinedRows(activeModelIds.value))

// const isFetching = computed(() => Object.values(modelLoadingById.value).some(Boolean))
const isFetching = computed(() => getCombinedIsLoading(activeModelIds.value))
const isAborted = computed(() => getCombinedIsAborted(activeModelIds.value))
const combinedProgress = computed(() => getCombinedProgress(activeModelIds.value))

const combinedErrors = computed(() =>
  combinedRows.value.filter((row) => rowHasAnyError(row)).map((row) => omit(row, ['predictionsByModelId'])),
)

const CombinedPredictTableRef = ref(null)

const selectedChemicals = ref([])
const selectedCount = computed(() => selectedChemicals.value?.length ?? 0)

const onCardSelectionChanged = (nodes) => {
  selectedChemicals.value = nodes.map((node) => node.data.chemical)
}

const canGroupByProperty = computed(() => {
  if (selectedBranches.value?.length < 2) return false
  const groupableIds = getGroupableModelIds(categorizedModels.value)
  return activeModelIds.value.some((id) => groupableIds.includes(id))
})

const visibleRows = computed(() =>
  displaySettings.value.showChemicalsWithNoEstimates
    ? combinedRows.value
    : combinedRows.value.filter((row) => rowHasAnyEstimate(row)),
)

const exportMenuProps = computed(() => ({
  getReport: () => CombinedPredictTableRef.value?.getExportData(),
  exportUrl: `${SERVICES_URL}/export_json_report`,
  showImages: displaySettings.value.showChemCell,
  fileName: 'predictions_export',
  title: 'Export report to file',
  count: selectedCount.value,
  loading: isFetching.value,
}))

const combinedProgressLabels = computed(() => getCombinedProgressLabels(activeModelIds.value))

const resetResults = (modelIds = activeModelIds.value) => {
  resetSelectedModels(modelIds)
}

const abortFetching = (modelIds = activeModelIds.value) => {
  abortSelectedModels(modelIds)
}

// const onLoadingChanged = (modelId, isLoading) => {
//   modelLoadingById.value = {
//     ...modelLoadingById.value,
//     [modelId]: isLoading,
//   }
// }

const fetchResults = (modelIds = activeModelIds.value) => {
  store.commit('SET_VIEW_STRUCTURES', false)
  return fetchSelectedModels({
    modelIds,
    chemicals: checkedStructuresWithCartPosition.value,
    // onLoadingChanged,
  })
}

const readyToCalculate = computed(() => {
  const hasChecked = checkedStructuresWithCartPosition.value.length
  const hasActiveModels = activeModelIds.value.length
  // const isNotLoading = !isFetching.value

  return hasChecked && hasActiveModels
})

const reportControlsStatus = computed(() => {
  if (isFetching.value) {
    return 'loading'
  }

  if (combinedRows.value.length) {
    return readyToCalculate.value ? 'stale' : 'fresh'
  }

  return readyToCalculate.value ? 'ready' : 'invalid'
})

const stopButtonId = 'combined-predict-stop-button'

const maybeOpenChemCart = () => {
  const noChecked = checkedStructuresWithCartPosition.value.length === 0
  const noResults = combinedRows.value.length === 0
  if (noChecked && noResults) store.commit('SET_VIEW_STRUCTURES', true)
}

onMounted(() => maybeOpenChemCart())

const proxy = getCurrentInstance().proxy

const isMobile = computed(() => {
  return proxy.$vuetify.breakpoint.xs
})
</script>

<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <CombinedPredictTableControls
          :display-settings.sync="displaySettings"
          :can-group-by-property="canGroupByProperty"
          :visible-rows-count="visibleRows.length"
          :rows-count="combinedRows.length"
          :export-menu-props="exportMenuProps"
          :show-warning-badge="!!combinedErrors?.length"
          :default-display-settings="defaultDisplaySettings"
          :selected-chemicals="selectedChemicals"
        >
          <template #left>
            <NestedCategorySelect
              :value.sync="selectedColumnIds"
              :activator-label="isMobile ? 'Models' : 'Select models'"
              :tree-items="categorizedModels"
              expanded-state-storage-key="combined-predict.models"
              :v-btn-props="{ class: 'border', depressed: true, minWidth: isMobile ? undefined : '17rem' }"
              selection-count-mode="branches"
              allow-initial-empty-selection
              @update:selectedBranches="(nodes) => getSelectedBranches(nodes)"
              @update:value="(value) => (selectedColumnIds = value)"
            />

            <ReportControls
              :status="reportControlsStatus"
              calculate-label="Calculate"
              clear-label="Reset"
              calculate-tooltip="Calculate predictions for chosen models and chemicals"
              clear-tooltip="Reset selected models (will clear the table below)"
              calculate-aria-label="Calculate predictions"
              clear-aria-label="Reset selected models"
              :ui="{ calculate: 'primary' }"
              @calculate="fetchResults(activeModelIds)"
              @clear="resetResults()"
            >
              <template
                v-if="isFetching"
                #before-clear
              >
                <v-btn
                  :id="stopButtonId"
                  :disabled="isAborted"
                  aria-label="Stop fetching predictions"
                  @click="abortFetching()"
                >
                  <v-icon>mdi-stop</v-icon>
                </v-btn>

                <v-tooltip
                  top
                  v-bind="defaultVTooltipProps"
                  :activator="`#${stopButtonId}`"
                >
                  <span>Stop fetching predictions</span>
                </v-tooltip>
              </template>
            </ReportControls>
          </template>

          <template #switch-showChemicalsWithNoEstimates-right>
            <v-menu
              v-if="combinedErrors?.length"
              :close-on-content-click="false"
              offset-y
              max-width="30rem"
              max-height="30rem"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  color="warning"
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  aria-label="Show prediction errors"
                >
                  <v-icon small>mdi-alert-circle-outline</v-icon>
                </v-btn>
              </template>

              <v-card class="pa-2">
                <v-card-title>Errors fetching predictions ({{ combinedErrors.length }})</v-card-title>
                <v-card-text>
                  <b-table-lite
                    :fields="[
                      { key: 'chemical', label: 'Structure' },
                      { key: 'errorsByModelId', label: 'Errors' },
                    ]"
                    :items="combinedErrors"
                    borderless
                    small
                  >
                    <template #cell(chemical)="data">
                      <div
                        class="text-break text-truncate"
                        style="max-width: 10rem"
                      >
                        {{ getChemicalTitle(data?.value) ?? 'N/A' }}
                      </div>
                    </template>

                    <template #cell(errorsByModelId)="data">
                      <div
                        class="d-flex flex-column"
                        style="gap: 1rem"
                      >
                        <div
                          v-for="(dataError, name) in formatModelErrorsForDisplay(data?.value)"
                          :key="name"
                          class="d-flex flex-column"
                        >
                          <span
                            v-html="name"
                            class="font-weight-bolder"
                          />

                          <span class="font-weight-lighter font-italic text-break">{{ dataError }}</span>
                        </div>
                      </div>
                    </template>
                  </b-table-lite>
                </v-card-text>
              </v-card>
            </v-menu>
          </template>
        </CombinedPredictTableControls>

        <div v-if="isFetching">
          <v-progress-linear
            class="position-relative"
            :indeterminate="combinedProgress == null"
            :value="combinedProgress ?? undefined"
          />

          <div
            class="position-absolute text-center caption text-muted w-100"
            style="z-index: 2"
          >
            <div
              v-for="(label, idx) in combinedProgressLabels"
              :key="idx"
            >
              {{ label }}
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="isAborted && isFetching"
        class="mt-4"
      >
        Aborting fetching predictions...
      </div>

      <div
        v-if="!isFetching && !visibleRows?.length"
        class="mt-15 text-center"
      >
        {{
          combinedErrors && combinedErrors?.length > 0
            ? "No results. Toggle 'Show empty' in the display options (with warning badge) to view details"
            : !checkedStructuresWithCartPosition?.length
              ? `Select chemicals ${!activeModelIds?.length ? 'and models' : ''} to generate predictions`
              : !activeModelIds?.length
                ? 'Select models to generate predictions'
                : ''
        }}
      </div>

      <CombinedPredictTable
        ref="CombinedPredictTableRef"
        :results="combinedRows"
        :loading="isFetching"
        :visible-column-ids="selectedColumnIds"
        :model-ids="activeModelIds"
        :categories-count="selectedBranches?.length"
        :display-settings="displaySettings"
        @selectionChanged="onCardSelectionChanged"
      />
    </TableCardLayout>
  </div>
</template>
